import { ServerDeployment, type ServerFeature } from '@affine/graphql';
import {
  backoffRetry,
  effect,
  Entity,
  fromPromise,
  LiveData,
} from '@toeverything/infra';
import { EMPTY, exhaustMap, mergeMap } from 'rxjs';

import type {
  ServerConfigStore,
  ServerConfigType,
} from '../stores/server-config';

type LowercaseServerFeature = Lowercase<ServerFeature>;
type ServerFeatureRecord = {
  [key in LowercaseServerFeature]: boolean;
};

export class ServerConfig extends Entity {
  readonly config$ = new LiveData<ServerConfigType | null>(null);

  readonly features$ = this.config$.map(config => {
    return config
      ? Array.from(new Set(config.features)).reduce((acc, cur) => {
          acc[cur.toLowerCase() as LowercaseServerFeature] = true;
          return acc;
        }, {} as ServerFeatureRecord)
      : null;
  });

  // DNB_FIX добавить переменную для включения/отключения изменения аккаунта
  readonly enabledChangeAccount$ = this.config$.map(config => {
    return config ? config.enableChangeAccount : true;
  });

  // DNB_FIX добавить переменную для включения/отключения авторизации по электронной почте
  readonly enableAuthEmail$ = this.config$.map(config => {
    return config ? config.enableAuthEmail : true;
  });

  readonly validations$ = this.config$.map(config => {
    return config ? config.validations : null;
  });

  readonly isSelfhosted$ = this.config$.selector(
    c => c?.type === ServerDeployment.Selfhosted
  );

  constructor(private readonly store: ServerConfigStore) {
    super();
  }

  revalidate = effect(
    exhaustMap(() => {
      return fromPromise<ServerConfigType>(signal =>
        this.store.fetchServerConfig(signal)
      ).pipe(
        backoffRetry({
          count: Infinity,
        }),
        mergeMap(config => {
          this.config$.next(config);
          return EMPTY;
        })
      );
    })
  );

  revalidateIfNeeded = () => {
    if (!this.config$.value) {
      this.revalidate();
    }
  };

  override dispose(): void {
    this.revalidate.unsubscribe();
  }
}
